// Zielliste einer Stufenkachel (Mehrzielmodus, Task F2). Die Zeile ist der Schalter, es gibt kein zweites
// Bedienelement: `ListItemButton role="checkbox"` mit Tastatur (Leertaste/Eingabe) und Klick. Das letzte
// gewählte Ziel einer Stufe und die Basisspalte (`lockedId`, Vertrag V.16) lassen sich nicht abwählen.
import React from "react";
import { List, ListItem, ListItemButton, ListItemIcon, ListItemText, Tooltip } from "@mui/material";
import CheckBoxOutlined from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlank from "@mui/icons-material/CheckBoxOutlineBlank";
import { useLang } from "../i18n";

// `source` (optional): `{ [targetId]: kennung[] }` der Werke, deren Katalogeintrag das Ziel setzt; `plantNames`: `{ kennung: name }`.
// Ohne `source` bleibt das DOM wie bisher.
function sourceHint(t, kennungen, plantNames) {
  const labels = kennungen.map(kennung => [plantNames?.[kennung], kennung].filter(Boolean).join(" "));
  if (kennungen.length >= 3) return { text: t("s1.targetSetByMany", { n: kennungen.length }), title: labels.join(", ") };
  return { text: t("s1.targetSetBy", { werke: labels.join(", ") }), title: "" };
}

export default function StageTargetList({ stageKey, code, rows, selected, lockedId = null, disabled, onToggle, source = null, plantNames = null }) {
  const { t } = useLang();
  return (
    <List dense disablePadding aria-label={t("s1.targetsOf", { stage: code })} sx={{ mt: 0.5 }}>
      {rows.map(row => {
        const on = selected.includes(row.targetId);
        const fixed = on && (selected.length <= 1 || row.targetId === lockedId);
        const origin = on && source?.[row.targetId]?.length ? sourceHint(t, source[row.targetId], plantNames) : null;
        const secondary = [row.region, row.isDefault ? t("s1.defaultTarget") : null].filter(Boolean).join(" · ");
        return (
          <ListItem key={row.targetId} disablePadding>
            <Tooltip title={fixed ? t(row.targetId === lockedId ? "s1.targetBase" : "s1.targetLast") : ""} placement="top" disableInteractive>
              <span style={{ display: "block", width: "100%" }}>
                <ListItemButton role="checkbox" aria-checked={on} aria-disabled={fixed || disabled || undefined} selected={on}
                  data-target={row.targetKey} disabled={disabled} disableRipple
                  onClick={() => { if (!fixed && !disabled) onToggle(stageKey, row.targetId); }}
                  sx={{ borderRadius: "var(--r-md, 6px)", px: 1, py: 0.5, minHeight: 36, color: "var(--t1)", fontFamily: "inherit",
                    "&.Mui-selected": { backgroundColor: "transparent" }, "&:hover": { backgroundColor: "var(--bg3, transparent)" } }}>
                  <ListItemIcon sx={{ minWidth: 28, color: on ? "var(--iio-signal, var(--accent))" : "var(--t3)" }}>
                    {on ? <CheckBoxOutlined fontSize="small" aria-hidden /> : <CheckBoxOutlineBlank fontSize="small" aria-hidden />}
                  </ListItemIcon>
                  <ListItemText primary={row.instanceName}
                    secondary={origin ? <>
                      {secondary}{secondary && " · "}
                      {origin.title
                        ? <Tooltip title={origin.title} placement="top"><span data-source-hint tabIndex={0}>{origin.text}</span></Tooltip>
                        : <span data-source-hint>{origin.text}</span>}
                    </> : secondary || undefined}
                    primaryTypographyProps={{ fontFamily: "inherit", fontSize: 14, fontWeight: 600 }}
                    secondaryTypographyProps={{ fontFamily: "inherit", fontSize: 11.5, color: "var(--t3)" }} />
                </ListItemButton>
              </span>
            </Tooltip>
          </ListItem>
        );
      })}
    </List>
  );
}
